import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $profileFeatureStore } from '@lib/store/profile';
import { $userAccountsStore, $userCardsStore, $userStore } from '@lib/store/userManagement';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import { UserType } from '@shared/api/rest/users';
import { RemoveAccountsModal, RemoveCardsModal, UserCards, UserInfo, UserInfoHeader, UserSidebar } from './widgets';
import { AccountTileSmall } from '@pages';
import { AddAccountsModal, AddCardsModal } from '@widgets';
import { Button, NoData, PageContainer, Spinner, Text } from '@shared/widgets';
import styles from './user.module.css';

export const UserPage = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouteHook();

  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showRemoveAccounts, setShowRemoveAccounts] = useState<boolean>(false);
  const [showAddCards, setShowAddCards] = useState<boolean>(false);
  const [showRemoveCards, setShowRemoveCards] = useState<boolean>(false);
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userStore = useUnit($userStore.store);
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  useEffect(() => {
    if (!profileFeatureStore.data?.userManagement.viewUserProfileDetails) navigate(RoutePaths.users.onNavigate());
  }, []);

  useEffect(() => {
    if (!id) return;
    $userStore.events.getUser(id);
    $userAccountsStore.events.getUserAccounts({ AssignedUserId: id });
  }, [id]);

  const unlockUserHandler = () => $userStore.events.unlockUser(userStore.data!.id);

  const accessAccounts = useMemo(
    () => userStore.data?.userType === UserType.MANAGER && profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );
  const accessCards = useMemo(
    () => userStore.data?.userType === UserType.USER && profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );

  return (
    <PageContainer title='User' backText='Back to all users'>
      <div className='flex justify-center'>
        {userStore.loading ? (
          <Spinner position />
        ) : !userStore.data || !profileFeatureStore.data?.userManagement.viewUserProfileDetails ? (
          <NoData />
        ) : (
          <>
            <UserInfo value={userStore.data}>
              <UserInfoHeader className='mb-12' user={userStore.data} />
              {userStore.data.userType !== UserType.ADMIN && (
                <>
                  {accessAccounts && (
                    <div className='mb-12'>
                      <div className='flex flex-wrap items-center justify-between  mb-6'>
                        <Text className='pr-4' weight='medium' size='2xl'>
                          Accounts ({userAccountsStore.data.length})
                        </Text>
                        <div className='flex items-center'>
                          {profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowAddAccounts(true)}>
                              Add
                            </Button>
                          )}
                          <AddAccountsModal
                            isOpen={showAddAccounts}
                            user={userStore.data}
                            accountsItems={userAccountsStore.data}
                            onClose={() => setShowAddAccounts(false)}
                            onSubmit={(data) => $userAccountsStore.events.addAccounts(data)}
                          />
                          {userAccountsStore.data.length > 0 && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowRemoveAccounts(true)}>
                              Remove
                            </Button>
                          )}
                          <RemoveAccountsModal show={showRemoveAccounts} userData={userStore.data} onClose={() => setShowRemoveAccounts(false)} />
                        </div>
                      </div>
                      {userAccountsStore.loading ? (
                        <Spinner />
                      ) : userAccountsStore.data.length > 0 ? (
                        <div className={clsx('flex flex-wrap -mx-4 -mb-8', userStore.data.locked && 'opacity-40 pointer-events-none select-none')}>
                          {userAccountsStore.data.map((item, idx) => (
                            <div key={idx} className={styles['accounts-list__col']}>
                              <AccountTileSmall key={idx} account={item} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <NoData text='No related records' />
                      )}
                    </div>
                  )}
                  {accessCards && (
                    <div className='mb-12'>
                      <div className='flex flex-wrap items-center justify-between  mb-6' title={'Cards' + ` (${userCardsStore.data.data.length})`}>
                        <Text className='pr-4' weight='medium' size='2xl'>
                          Cards ({userCardsStore.data.data.length})
                        </Text>
                        <div className='flex items-center'>
                          <Button color='outlined' className='mr-3' onClick={() => setShowAddCards(true)}>
                            Add
                          </Button>
                          <AddCardsModal
                            isOpen={showAddCards}
                            user={userStore.data}
                            cardsItems={userCardsStore.data.data}
                            onClose={() => setShowAddCards(false)}
                            onSubmit={(data) => $userCardsStore.events.addCards(data)}
                          />
                          {userCardsStore.data.data.length > 0 && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowRemoveCards(true)}>
                              Remove
                            </Button>
                          )}
                          <RemoveCardsModal show={showRemoveCards} userData={userStore.data} onClose={() => setShowRemoveCards(false)} />
                        </div>
                      </div>
                      <UserCards userId={userStore.data.id} />
                    </div>
                  )}
                </>
              )}
            </UserInfo>
            <div className={styles['with-sidebar__sidebar']}>
              {userStore.data.locked && (
                <div className='mb-5 text-center'>
                  <Button color='primary' className='min-w-48' onClick={unlockUserHandler}>
                    Unlock
                  </Button>
                </div>
              )}
              <div className={styles[userStore.data.locked ? 'user-blocked' : '']}>
                <UserSidebar user={userStore.data} />
              </div>
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};
